import {Injectable} from "@angular/core";
import {FormService} from "../../../umbrella-common/services/form.service";
import {FormBuilder, Validators} from "@angular/forms";
import {Order, PriceFactor, Product, StandardName} from "../../../store/state/order.state";
import {distinctUntilChanged, takeWhile} from "rxjs/operators";
import {AppState} from "../../../store/state/app.state";
import {Store} from "@ngrx/store";
import {OrderActions} from "../../../store/actions/order.actions";
import {BaseOrderFormService} from "../common/base-order-form.service";
import {UmbrellaValidators} from "../../../umbrella-common/validation/umbrella-validators";
import {List} from "immutable";
import {TranslateService} from "@ngx-translate/core";

@Injectable()
export class PriceFactorsFormService extends FormService implements BaseOrderFormService {

	private allPriceFactors: PriceFactor[];
	private controlNamesInOrder: string[] = [];

	constructor(private fb: FormBuilder, private store: Store<AppState>, translate: TranslateService) {
		super(translate);
	}

	protected buildFormFor(formKey: string, [priceFactors, product]: [PriceFactor[], Product]) {
		this.initControlNamesInOrder(priceFactors, product);
		const pfSet = new Set(this.allPriceFactors);
		priceFactors.forEach(pf => pfSet.add(pf));
		this.allPriceFactors = Array.from(pfSet);

		const form = this.fb.group({});
		priceFactors.forEach(pf => {
			const control = this.fb.control(this.getAnswer(pf.type, product), Validators.required);
			form.addControl(pf.type + product.standard.name + '', control);

			pf.attachedQuestions.forEach(attachedQuestion => {
				if (this.getAnswer(pf.type, product) === attachedQuestion.parentQuestionAnswerValue) {
					this.addPriceFactorAttachedQuestion(product, pf.type, attachedQuestion.type, form);
				}
			});

			control.valueChanges.pipe(takeWhile(() => this.isSubFormInitialized(formKey)), distinctUntilChanged()).subscribe(positiveAnswer => {
				this.store.dispatch(OrderActions.PRICE_FACTOR_QUESTION_ANSWERED({
					standardName: product.standard.name,
					priceFactorType: pf.type,
					positiveAnswer,
					priceFactorQuestion: pf.question
				}));

				pf.attachedQuestions.forEach(attachedQuestion => {
					if (positiveAnswer === attachedQuestion.parentQuestionAnswerValue) {
						this.addPriceFactorAttachedQuestion(product, pf.type, attachedQuestion.type, form);
					} else {
						this.removePriceFactorAttachedQuestion(product, pf.type, attachedQuestion.type, form);
					}
				});
			});
		});

		return form;
	}

	getControlNamesInOrder(): string[] {
		return this.controlNamesInOrder;
	}

	getAttachedQuestionFormControlNamePrefix(priceFactorType: string, attachedQuestionType: string) {
		return priceFactorType + '-' + attachedQuestionType;
	}

	private addPriceFactorAttachedQuestion(product: Product, priceFactorType: string, attachedQuestionType: string, form) {
		const attachedQuestionControl = this.fb.control(this.getAttachedQuestionAnswer(priceFactorType, attachedQuestionType, product), [Validators.required, UmbrellaValidators.textLengthBetween(2, 100)]);
		form.addControl(this.getAttachedQuestionFormControlNamePrefix(priceFactorType, attachedQuestionType) + product.standard.name, attachedQuestionControl);

		attachedQuestionControl.valueChanges.pipe(takeWhile(() => this.isSubFormInitialized(product.standard.name)), distinctUntilChanged()).subscribe(answer => {
			this.store.dispatch(OrderActions.PRICE_FACTOR_ATTACHED_QUESTION_ANSWERED({
				standardName: product.standard.name,
				priceFactorType,
				attachedQuestionType,
				answer
			}));
		});
	}

	private removePriceFactorAttachedQuestion(product: Product, priceFactorType: string, attachedQuestionType: string, form) {
		form.removeControl(this.getAttachedQuestionFormControlNamePrefix(priceFactorType, attachedQuestionType) + product.standard.name);
		this.store.dispatch(OrderActions.PRICE_FACTOR_ATTACHED_QUESTION_ANSWERED({
			standardName: product.standard.name,
			priceFactorType,
			attachedQuestionType,
			answer: null
		}));
	}

	fillOrder(order: Order): Order {
		this.getAllFormKeys().filter(formKey => this.isSubFormInitialized(formKey)).forEach(formKey => {
			const product = order.products.find(p => p.standard.name === formKey);
			const formControlNames = this.getPriceFactorFormControlNames(formKey);
			product.priceFactors = formControlNames.map(controlName => {
				const factor = this.allPriceFactors.find(pf => pf.type == controlName.replace(formKey, '') && pf.standardName == formKey);
				factor.positiveAnswer = this.getValueFor(controlName, formKey);
				factor.attachedQuestions.forEach(q => {
					q.answer = this.getValueOrNull(formKey, this.getAttachedQuestionFormControlNamePrefix(factor.type, q.type));
				});
				return factor;
			});

			if (formKey == StandardName.ISO_27001) {
				let iso27701 = order.products.find(p => p.standard.name === StandardName.ISO_27701);
				if (iso27701) {
					iso27701.priceFactors = product.priceFactors;
				}
			}
		});
		return order;
	}

	private getPriceFactorFormControlNames(formKey): string[] {
		return this.getSubFormControlNames(formKey).filter(controlName => this.allPriceFactors.find(pf => pf.type == controlName.replace(formKey, '') && pf.standardName == formKey) != null);
	}

	anyPriceFactorInvalid(product: Product): boolean {
		const controls = this.getAllControls(product.standard.name);
		return Object.keys(controls).some(controlName => this.isInvalid(product.standard.name, controlName));
	}

	private getAnswer(priceFactorType: string, product: Product): boolean | null {
		const priceFactor = product.priceFactors.find(pf => pf.type === priceFactorType);
		return !!priceFactor ? priceFactor.positiveAnswer : null;
	}

	private getAttachedQuestionAnswer(priceFactorType: string, attachedQuestionType: string, product: Product): boolean | null {
		const priceFactor = product.priceFactors.find(pf => pf.type === priceFactorType);
		const attachedQuestion = (priceFactor && priceFactor.attachedQuestions) ? priceFactor.attachedQuestions.find(q => q.type === attachedQuestionType) : null;
		return !!attachedQuestion ? attachedQuestion.answer : null;
	}

	private initControlNamesInOrder(priceFactors: PriceFactor[], product: Product) {
		const controlNames: string[] = List(priceFactors).flatMap(pf => {
			return [pf.type + product.standard.name + '']
				.concat(pf.attachedQuestions == null ? [] : pf.attachedQuestions.map(q => this.getAttachedQuestionFormControlNamePrefix(pf.type, q.type) + product.standard.name))
		}).toArray();

		this.controlNamesInOrder = this.controlNamesInOrder.concat(controlNames);
	}

	addAttachedQuestionTypeIfNeeded(product: Product, priceFactors: PriceFactor[]) {
		const form = this.getForm(product.standard.name);
		priceFactors.forEach(pf => {
			pf.attachedQuestions.forEach(attachedQuestion => {
				if (this.getAnswer(pf.type, product) === attachedQuestion.parentQuestionAnswerValue) {
					this.addPriceFactorAttachedQuestion(product, pf.type, attachedQuestion.type, form);
				}
			});
		});
	}
}
